import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { asyncScheduler, Observable, Subject, delay, distinctUntilChanged, map, throttleTime } from 'rxjs';
import { Line } from '../../shared/models/line';
import { Store } from './store.model';

export interface LineStoreState {
    lines: Map<string, Line>;
}

@Injectable({
    providedIn: 'root'
})
export class LineStore extends Store<LineStoreState> {
    private _triggerRemoveGarageCode$: Subject<string> = new Subject();
    private _removeGarageCode$ = this._triggerRemoveGarageCode$.asObservable();

    constructor() {
        super({
            lines: undefined
        });

        this._removeGarageCode$
            .pipe(
                delay(1000)
            )
            .subscribe((garageCode: string) => {
                if (!this.stateSnapshot.lines) {
                    return;
                }

                this.stateSnapshot.lines.forEach((line: Line, id: string) => {
                    if (line.contains(garageCode)) {
                        this.stateSnapshot.lines.delete(id);
                    }
                });

                // reset state variable to send correct empty state to subscribing components
                // if there is no garage selected the lines.values() should be undefined not []
                if (this.stateSnapshot.lines.size === 0) {
                    this.stateSnapshot.lines = undefined;
                }

                this.setState({
                    ...this.stateSnapshot
                });
            });
    }

    public resetData() {
        this.setState({
            ...this.stateSnapshot,
            lines: undefined
        });
    }

    public getLinesState(): Observable<Line[]> {
        return super.state$.pipe(
            map(state => state.lines !== undefined ? Array.from(state.lines.values()) : null),
            throttleTime(500, asyncScheduler, { trailing: true }));
    }

    public getLineState(lineId: string): Observable<Line> {
        return super.state$.pipe(
            map(state => state?.lines?.get(lineId)),
            distinctUntilChanged(),
        );
    }

    public update(linesToUpdate: Line[]) {
        if (!linesToUpdate) {
            return;
        }

        let lines = super.stateSnapshot.lines;

        if (!lines) {
            lines = new Map<string, Line>();
        }

        _.each(linesToUpdate, (line) => {
            if (!line.routeNumber) {
                return;
            }

            const existingLine: Line = this.stateSnapshot.lines?.get(line.id);

            if (existingLine !== undefined) {
                if (!existingLine.isEqual(line)) {
                    Object.assign(existingLine, line);
                }
            } else {
                lines.set(line.id, line);
            }
        });

        this.setState({
            ...this.stateSnapshot,
            lines
        });
    }

    public remove(garageCode: string) {
        this._triggerRemoveGarageCode$.next(garageCode);
    }

}
